import { FC, ReactNode } from "react";

import { useInView } from "../../hooks/useInView";
import { prefersReducedMotion } from "../../utils/prefersReducedMotion";

// Distance the block travels while fading in. Deliberately small: the page should feel
// alive on scroll, not animated.
const TRAVEL = "8px";
const DURATION = 500;
// Blocks that enter the viewport together (above the fold on load) get a short cascade.
// Capped so a long page never ends up waiting on a growing delay.
const STAGGER = 90;
const MAX_STAGGER_STEPS = 4;

interface RevealProps {
	children: ReactNode;
	/** Position of the block on the page, used to stagger the initial cascade. */
	index?: number;
	/** Needed where the wrapper sits inside a layout — a grid cell that stretches, say. */
	className?: string;
}

const Reveal: FC<RevealProps> = ({ children, index = 0, className }) => {
	// Trigger slightly before the block reaches the bottom edge, so it has finished fading in
	// by the time it is properly in view.
	const [ref, isVisible] = useInView<HTMLDivElement>({
		disabled: prefersReducedMotion(),
		rootMargin: "0px 0px -10% 0px",
	});

	return (
		<div
			ref={ref}
			className={className}
			data-reveal={isVisible ? "shown" : "hidden"}
			style={{
				opacity: isVisible ? 1 : 0,
				// Left undefined once shown so no transform lingers on the element: a transform
				// creates a containing block, which would trap `position: sticky` children.
				transform: isVisible ? undefined : `translateY(${TRAVEL})`,
				transition: `opacity ${DURATION}ms ease-out, transform ${DURATION}ms ease-out`,
				transitionDelay: `${Math.min(index, MAX_STAGGER_STEPS) * STAGGER}ms`,
			}}
		>
			{children}
		</div>
	);
};

export { Reveal };
